import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
  } from 'react';
  import { useParams, useLocation } from 'react-router-dom';
  import { doc, getDoc } from 'firebase/firestore';
  import { db } from '../lib/firebase';
  import { fetchUserWorkspaces } from '../features/workspace/fetchUserWorkspaces';
  import { Workspace } from '../features/workspace/types';
  import {
    DEFAULT_WORKSPACE_BASE_COLOR_KEY,
    isWorkspaceBaseColorKey,
  } from '../theme/workspaceBaseColors';
  import { useAuthContext } from './AuthContext';
  import { 
    getWorkspaceFileStats, 
    FileStatsData, 
    verifyAndFixFileStats 
  } from '../features/workspace/fileStatsUtils';
  
  interface WorkspaceContextType {
    workspaces: Workspace[];
    loading: boolean;
    refresh: () => void;
    selectedWorkspace: Workspace | null;
    // ファイル統計関連
    fileStats: FileStatsData | null;
    fileStatsLoading: boolean;
    refreshFileStats: () => Promise<void>;
    verifyFileStats: () => Promise<{ wasFixed: boolean; beforeStats: FileStatsData | null; afterStats: FileStatsData }>;
  }
  
  const WorkspaceContext = createContext<WorkspaceContextType>({
    workspaces: [],
    loading: true,
    refresh: () => {},
    selectedWorkspace: null,
    fileStats: null,
    fileStatsLoading: false,
    refreshFileStats: async () => {},
    verifyFileStats: async () => ({ wasFixed: false, beforeStats: null, afterStats: { totalFileSize: 0, fileCount: 0, lastFileSizeUpdate: new Date() } }),
  });
  
  export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => {
    const { user, initializing } = useAuthContext();
    const { workspaceId } = useParams();
    const location = useLocation();

    const NON_WORKSPACE_PREFIXES = useMemo(() => new Set([
      'cg', 'p', 'sys', 'admin', 'home', 'open', 'widget',
    ]), []);

    const earlyWorkspaceId = useMemo(() => {
      if (workspaceId) return workspaceId;
      const firstSegment = location.pathname.split('/').filter(Boolean)[0];
      if (!firstSegment || NON_WORKSPACE_PREFIXES.has(firstSegment)) return undefined;
      return firstSegment;
    }, [workspaceId, location.pathname, NON_WORKSPACE_PREFIXES]);

    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [loading, setLoading] = useState(true);
    const loadingRef = useRef(false);
    
    // ファイル統計関連の状態
    const [fileStats, setFileStats] = useState<FileStatsData | null>(null);
    const [fileStatsLoading, setFileStatsLoading] = useState(false);
    const fileStatsLoadingRef = useRef(false);
    const fileStatsWorkspaceIdRef = useRef<string | null>(null);
  
    // ファイル統計をロードする関数
    const loadFileStats = useCallback(async (workspaceId: string) => {
      if (fileStatsLoadingRef.current) {
        return;
      }

      fileStatsLoadingRef.current = true;
      fileStatsWorkspaceIdRef.current = workspaceId;
      setFileStatsLoading(true);

      try {
        const stats = await getWorkspaceFileStats(workspaceId);
        // route が切り替わった後に古いworkspaceの結果を反映しない
        if (fileStatsWorkspaceIdRef.current !== workspaceId) return;
        setFileStats(stats);
      } catch (error) {
        console.error('🏢 WorkspaceContext: ファイル統計読み込みエラー:', error);
        if (fileStatsWorkspaceIdRef.current === workspaceId) {
          setFileStats(null);
        }
      } finally {
        if (fileStatsWorkspaceIdRef.current === workspaceId) {
          setFileStatsLoading(false);
          fileStatsLoadingRef.current = false;
        }
      }
    }, []);

    const quickFetchedIdRef = useRef<string | null>(null);

    const quickFetchCurrentWorkspace = useCallback(async (targetWorkspaceId: string) => {
      if (!targetWorkspaceId || quickFetchedIdRef.current === targetWorkspaceId) return;
      quickFetchedIdRef.current = targetWorkspaceId;

      try {
        const wsRef = doc(db, 'workspaces', targetWorkspaceId);
        const wsSnap = await getDoc(wsRef);

        if (wsSnap.exists()) {
          const data = wsSnap.data();
          const ws: Workspace = {
            id: targetWorkspaceId,
            name: data.name || targetWorkspaceId,
            visibility: data.visibility || 0,
            publish: data.publish || false,
            publishId: data.publishId || undefined,
            baseColorKey: isWorkspaceBaseColorKey(data.baseColorKey)
              ? data.baseColorKey
              : DEFAULT_WORKSPACE_BASE_COLOR_KEY,
            companyId: data.companyId || undefined,
            billingAccountId: data.billingAccountId || undefined,
            planId: data.planId || undefined,
            seriesId: data.seriesId || undefined,
            offerId: data.offerId || undefined,
            entitlementOverrides: data.entitlementOverrides || undefined,
            stripeSubscriptionId: data.stripeSubscriptionId || undefined,
            stripeCustomerId: data.stripeCustomerId || undefined,
            stripeSubscriptionStatus: data.stripeSubscriptionStatus || undefined,
            billingStatus: data.billingStatus || undefined,
            cancelAtPeriodEnd: typeof data.cancelAtPeriodEnd === 'boolean' ? data.cancelAtPeriodEnd : undefined,
            currentPeriodEnd: data.currentPeriodEnd?.toDate ? data.currentPeriodEnd.toDate() : undefined,
            pastDueSince: data.pastDueSince?.toDate ? data.pastDueSince.toDate() : undefined,
            graceUntil: data.graceUntil?.toDate ? data.graceUntil.toDate() : undefined,
            planChangePendingPlanId: data.planChangePendingPlanId || undefined,
            iconPath: data.iconPath || undefined,
            order: 0,
            createdBy: data.createdBy,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            parentWorkspaceId: data.parentWorkspaceId || undefined,
            notificationChannels: data.notificationChannels || undefined,
            notificationRules: data.notificationRules || undefined,
            crawlReferencedUrls: typeof data.crawlReferencedUrls === 'boolean' ? data.crawlReferencedUrls : undefined,
          };

          setWorkspaces(prev => {
            if (prev.some(w => w.id === targetWorkspaceId)) return prev;
            return [ws, ...prev];
          });
          setLoading(false);
        }
      } catch (e) {
        console.warn('quick workspace fetch failed:', e);
      }
    }, []);

    const loadWorkspaces = useCallback(async () => {
      if (!user) {
        return;
      }

      if (loadingRef.current) {
        return;
      }

      loadingRef.current = true;

      try {
        const list = await fetchUserWorkspaces(user.uid);

        setWorkspaces(list);

      } catch (error) {
        console.error('🏢 [WorkspaceContext] ワークスペース読み込みエラー:', {
          error,
          code: (error as any)?.code,
          message: (error as any)?.message,
          userId: user?.uid,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    }, [user]);
  
    useEffect(() => {
      // 認証復元前に loading=false にすると、一覧が空のまま「ワークスペースなし」になる
      if (initializing) return;
      if (user) {
        if (earlyWorkspaceId) {
          quickFetchCurrentWorkspace(earlyWorkspaceId);
        }
        loadWorkspaces();
      } else {
        setLoading(false);
        loadingRef.current = false;
      }
    }, [user, initializing, earlyWorkspaceId, loadWorkspaces, quickFetchCurrentWorkspace]);

    // 選択されたワークスペースが変更された時にファイル統計をロード
    useEffect(() => {
      if (earlyWorkspaceId) {
        loadFileStats(earlyWorkspaceId);
      } else {
        fileStatsWorkspaceIdRef.current = null;
        setFileStats(null);
        setFileStatsLoading(false);
        fileStatsLoadingRef.current = false;
      }
    }, [earlyWorkspaceId, loadFileStats]);
  
    // IDに対応するワークスペース情報を取得
    const selectedWorkspace = useMemo(() => {
      const wsId = earlyWorkspaceId;
      const workspace = workspaces.find((w) => w.id === wsId) || null;
      return workspace;
    }, [workspaces, earlyWorkspaceId]);

    // ファイル統計を手動でリフレッシュする関数
    const refreshFileStats = useCallback(async () => {
      if (workspaceId) {
        await loadFileStats(workspaceId);
      }
    }, [workspaceId, loadFileStats]);

    // ファイル統計の検証と修正を行う関数
    const verifyFileStats = useCallback(async () => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      
      const result = await verifyAndFixFileStats(workspaceId);
      
      // 修正が行われた場合は最新の統計を再読み込み
      if (result.wasFixed) {
        await loadFileStats(workspaceId);
      }
      
      return result;
    }, [workspaceId, loadFileStats]);
  
    return (
      <WorkspaceContext.Provider
        value={{
          workspaces,
          loading,
          refresh: loadWorkspaces,
          selectedWorkspace,
          fileStats,
          fileStatsLoading,
          refreshFileStats,
          verifyFileStats,
        }}
      >
        {children}
      </WorkspaceContext.Provider>
    );
  };
  
  export const useWorkspaceContext = () => useContext(WorkspaceContext);
  